import React, { useEffect, useState } from 'react';
import {
  Button,
  Combobox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Radio,
  RadioGroup,
  RadioGroupOnChangeData,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Switch,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  AddinSettings,
  StyleProfile,
  isValidBaseUrl,
  loadSettings,
  saveSettings,
  loadStyleProfile,
  saveStyleProfile,
} from '../settings';
import {
  reconnectGraphAdapter,
  disconnectGraphAdapter,
  getConnectedAccountName,
} from '../mailbox-adapter';
import type { ReconnectIntent } from '../mailbox-adapter';
import { StyleProfileEditor } from './StyleProfileEditor';
import { listModels, isLoopbackFromHostedOrigin } from '../ai-client';
import {
  OrgPolicy,
  PolicyField,
  applyPolicy,
  getHiddenFields,
  getLockedFields,
  isPolicyActive,
  loadPolicy,
} from '../policy';

export interface SettingsPanelProps {
  onSave: () => void;
  onClose: () => void;
}

type SettingsTab = 'ai' | 'mailbox' | 'style' | 'advanced';

export function SettingsPanel({ onSave, onClose }: SettingsPanelProps) {
  const [tab, setTab] = useState<SettingsTab>('ai');
  const [settings, setSettings] = useState<AddinSettings>(loadSettings());
  const [policy, setPolicy] = useState<OrgPolicy>({});
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  // Bug 5: refresh reloaded models but showed nothing, so it felt broken.
  // Confirm every load outcome explicitly (count, or an empty-endpoint hint).
  const [modelsFeedback, setModelsFeedback] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [mailboxFeedback, setMailboxFeedback] = useState<{
    intent: 'success' | 'error';
    text: string;
  } | null>(null);
  const [styleProfile, setStyleProfile] = useState<StyleProfile | null>(() => loadStyleProfile());

  useEffect(() => {
    let cancelled = false;
    void getConnectedAccountName(loadSettings()).then((name) => {
      if (!cancelled) setAccountName(name);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleReconnect(intent: ReconnectIntent) {
    setReconnecting(true);
    setMailboxFeedback(null);
    try {
      const next = await reconnectGraphAdapter(loadSettings(), intent);
      const name = await getConnectedAccountName(loadSettings());
      setAccountName(name);
      // NAA reconnection frequently returns silently (no popup); confirm the
      // outcome explicitly so the button never appears to do nothing.
      if (next.type === 'graph') {
        setMailboxFeedback({
          intent: 'success',
          text:
            intent === 'expand_permissions'
              ? `Mailbox permissions granted${name ? ` for ${name}` : ''}.`
              : `Reconnected${name ? ` as ${name}` : ''}.`,
        });
      } else {
        setMailboxFeedback({ intent: 'error', text: 'Reconnection failed. Please try again.' });
      }
    } catch {
      setMailboxFeedback({ intent: 'error', text: 'Reconnection failed. Please try again.' });
    } finally {
      setReconnecting(false);
    }
  }

  async function handleDisconnect() {
    setReconnecting(true);
    setMailboxFeedback(null);
    try {
      await disconnectGraphAdapter(loadSettings());
      setAccountName(null);
      setMailboxFeedback({ intent: 'success', text: 'Disconnected.' });
    } finally {
      setReconnecting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    loadPolicy().then((orgPolicy) => {
      if (cancelled) return;
      setPolicy(orgPolicy);
      setSettings((current) => applyPolicy(current, orgPolicy));
      setPolicyLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const lockedFields = getLockedFields(policy);
  const hiddenFields = getHiddenFields(policy);
  const isLocked = (field: PolicyField) => lockedFields.includes(field);
  const isHidden = (field: PolicyField) => hiddenFields.includes(field);

  const loadModels = async () => {
    setLoadingModels(true);
    setModelsError(null);
    setModelsFeedback(null);
    try {
      const list = await listModels(settings);
      setModels(list);
      setModelsFeedback(
        list.length > 0
          ? `${list.length} model${list.length === 1 ? '' : 's'} loaded.`
          : 'This endpoint returned no models — enter the model name manually.'
      );
    } catch (e) {
      setModelsError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoadingModels(false);
    }
  };

  // Lazy load: only fetch /models once Settings is open (this component is
  // mounted) and the policy has resolved — never at app boot, and never for
  // a locked/hidden model field (no point, the value is fixed).
  useEffect(() => {
    if (!policyLoaded) return;
    if (isLocked('model') || isHidden('model')) return;
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyLoaded]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    // Re-apply the policy so a locked field's imposed value is always what
    // gets persisted, regardless of the (disabled) input's transient state.
    const toSave = applyPolicy(settings, policy);
    if (!isValidBaseUrl(toSave.baseUrl)) {
      setError('Base URL must use HTTPS (HTTP allowed only for localhost)');
      setSaving(false);
      return;
    }
    try {
      await saveSettings(toSave);
      if (styleProfile) {
        // Budget is enforced inside saveStyleProfile; surface its error without
        // losing the just-saved provider settings.
        await saveStyleProfile(styleProfile);
      }
      onSave();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2>Settings</h2>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {isPolicyActive(policy) && (
        <MessageBar intent="info">
          <MessageBarBody>Some settings are managed by your organization</MessageBarBody>
        </MessageBar>
      )}
      <TabList
        selectedValue={tab}
        onTabSelect={(_: SelectTabEvent, d: SelectTabData) => setTab(d.value as SettingsTab)}
      >
        <Tab value="ai">AI Model</Tab>
        <Tab value="mailbox">Mailbox</Tab>
        <Tab value="style">Writing Style</Tab>
        <Tab value="advanced">Advanced</Tab>
      </TabList>

      {/* Appearance (Axis 12: dark mode) — deliberately kept outside the tab
          content below and not gated by `tab`, so it stays visible no matter
          which settings tab is selected. */}
      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
        <Field label="Appearance" hint="Choose the task pane's color theme.">
          <RadioGroup
            layout="horizontal"
            value={settings.themePreference}
            onChange={(_, data: RadioGroupOnChangeData) =>
              setSettings((s) => ({
                ...s,
                themePreference: data.value as AddinSettings['themePreference'],
              }))
            }
          >
            <Radio value="system" label="System" />
            <Radio value="light" label="Light" />
            <Radio value="dark" label="Dark" />
          </RadioGroup>
        </Field>
      </div>

      {tab === 'ai' && (
        <div style={{ marginTop: '12px' }}>
          {!isHidden('baseUrl') && (
            <Field
              label="Base URL"
              // The default endpoint is a local Ollama, which only works when
              // the add-in is itself served from localhost — from a hosted
              // origin the browser blocks the call (CORS + Chromium's
              // private-network gate) and the only symptom is a failed request.
              // Say so at the point of configuration rather than at first use.
              validationState={isLoopbackFromHostedOrigin(settings.baseUrl) ? 'warning' : 'none'}
              validationMessage={
                isLoopbackFromHostedOrigin(settings.baseUrl)
                  ? 'This add-in is hosted, so it cannot reach a model server on your machine. Use an HTTPS-reachable endpoint.'
                  : undefined
              }
            >
              <Input
                value={settings.baseUrl}
                disabled={isLocked('baseUrl')}
                onChange={(_, d) => setSettings((s) => ({ ...s, baseUrl: d.value }))}
              />
            </Field>
          )}
          {!isHidden('apiKey') && (
            <Field label="API Key">
              <Input
                type="password"
                value={settings.apiKey}
                disabled={isLocked('apiKey')}
                onChange={(_, d) => setSettings((s) => ({ ...s, apiKey: d.value }))}
              />
            </Field>
          )}
          {!isHidden('model') && (
            <Field label="Model">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Combobox
                  aria-label="Model"
                  freeform
                  disabled={isLocked('model')}
                  value={settings.model}
                  selectedOptions={[settings.model]}
                  onOptionSelect={(_, data) => {
                    // Freeform typing that no longer matches any option fires this
                    // with both fields undefined (internal clearSelection) — the
                    // free-text value is already tracked via onChange, so ignore it.
                    if (data.optionValue === undefined && data.optionText === undefined) {
                      return;
                    }
                    setSettings((s) => ({ ...s, model: data.optionText ?? (data.optionValue as string) ?? '' }));
                  }}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, model: (e.target as HTMLInputElement).value }))
                  }
                >
                  {models.map((m) => (
                    <Option key={m} value={m}>
                      {m}
                    </Option>
                  ))}
                </Combobox>
                {loadingModels ? (
                  <Spinner size="tiny" />
                ) : (
                  <Button
                    size="small"
                    disabled={isLocked('model')}
                    onClick={loadModels}
                  >
                    Refresh models
                  </Button>
                )}
              </div>
              {modelsError && (
                <MessageBar intent="warning">
                  <MessageBarBody>Could not load models: {modelsError}</MessageBarBody>
                </MessageBar>
              )}
              {!modelsError && modelsFeedback && (
                <MessageBar intent={models.length > 0 ? 'success' : 'info'}>
                  <MessageBarBody>{modelsFeedback}</MessageBarBody>
                </MessageBar>
              )}
            </Field>
          )}
          <Field
            label="Auto-analyze emails"
            hint="Automatically classify each email when opened. Sends the email's content to the configured AI endpoint."
          >
            <Switch
              checked={settings.autoAnalyze}
              onChange={(_, d) => setSettings((s) => ({ ...s, autoAnalyze: d.checked }))}
            />
          </Field>
        </div>
      )}
      {tab === 'mailbox' && (
        <div style={{ marginTop: '12px' }}>
          <h3>Mailbox access</h3>
          {accountName ? (
            <Text size={200} style={{ opacity: 0.8 }}>Connected as {accountName}</Text>
          ) : (
            <Text size={200} style={{ opacity: 0.8 }}>No account connected</Text>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            {reconnecting ? (
              <Spinner size="small" />
            ) : (
              <>
                <Button onClick={() => void handleReconnect('switch_account')}>
                  Switch account
                </Button>
                <Button onClick={() => void handleReconnect('expand_permissions')}>
                  Re-grant permissions
                </Button>
                <Button onClick={() => void handleDisconnect()}>Disconnect</Button>
              </>
            )}
          </div>
          {mailboxFeedback && (
            <div style={{ marginTop: '8px' }}>
              <MessageBar intent={mailboxFeedback.intent}>
                <MessageBarBody>{mailboxFeedback.text}</MessageBarBody>
              </MessageBar>
            </div>
          )}
        </div>
      )}
      {tab === 'style' && (
        <div style={{ marginTop: '12px' }}>
          <h3>Writing style</h3>
          <StyleProfileEditor value={styleProfile} onChange={setStyleProfile} />
        </div>
      )}
      {tab === 'advanced' && (
        <div style={{ marginTop: '12px' }}>
          <Field
            label="Graph client ID (optional)"
            hint="Overrides the built-in Entra app registration. Leave empty to use the default."
          >
            <Input
              value={settings.graphClientId}
              onChange={(_, d) => setSettings((s) => ({ ...s, graphClientId: d.value }))}
            />
          </Field>
          {!isHidden('purviewFilterEnabled') && (
            <Field
              label="Block AI on classified emails"
              hint="Checks each email's Microsoft Purview sensitivity label (and, when no label can be read, its content) before sending anything to the AI endpoint. Off restores the previous, unfiltered behavior."
            >
              <Switch
                checked={settings.purviewFilterEnabled}
                disabled={isLocked('purviewFilterEnabled')}
                onChange={(_, d) => setSettings((s) => ({ ...s, purviewFilterEnabled: d.checked }))}
              />
            </Field>
          )}
          <Field
            label="Redact sensitive content before sending to AI"
            hint="Strips likely email addresses, phone numbers, SSNs, card numbers, and API keys/tokens from outgoing messages before they reach the AI endpoint. Deterministic, local pattern matching only — no AI involved. Off restores the previous, unredacted behavior."
          >
            <Switch
              checked={settings.redactionEnabled}
              onChange={(_, d) => setSettings((s) => ({ ...s, redactionEnabled: d.checked }))}
            />
          </Field>
          <h3>Telemetry</h3>
          {!isHidden('telemetryEnabled') && (
            <Field
              label="Send operational telemetry"
              hint="Off by default — no data is collected or sent unless you turn this on. When enabled, structural details only (tool calls, LLM call duration, token counts, model name) are exported to the endpoint below — never email content, prompts, or completions."
            >
              <Switch
                checked={settings.telemetryEnabled}
                disabled={isLocked('telemetryEnabled')}
                onChange={(_, d) => setSettings((s) => ({ ...s, telemetryEnabled: d.checked }))}
              />
            </Field>
          )}
          {!isHidden('telemetryEndpoint') && (
            <Field
              label="Telemetry endpoint (OTLP/HTTP)"
              hint="Any OTLP-compatible backend — e.g. a self-hosted Langfuse instance's OTLP route. No vendor is built in."
            >
              <Input
                value={settings.telemetryEndpoint}
                disabled={isLocked('telemetryEndpoint')}
                onChange={(_, d) => setSettings((s) => ({ ...s, telemetryEndpoint: d.value }))}
              />
            </Field>
          )}
          {!isHidden('telemetryToken') && (
            <Field
              label="Telemetry token"
              hint="Sent verbatim as the Authorization header — e.g. 'Basic <base64(publicKey:secretKey)>' for Langfuse, or 'Bearer <token>' for another OTLP-compatible backend."
            >
              <Input
                type="password"
                value={settings.telemetryToken}
                disabled={isLocked('telemetryToken')}
                onChange={(_, d) => setSettings((s) => ({ ...s, telemetryToken: d.value }))}
              />
            </Field>
          )}
        </div>
      )}

      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        {saving ? (
          <Spinner size="small" />
        ) : (
          <>
            <Button appearance="primary" onClick={handleSave}>Save</Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}
